import { ChangeEvent, FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAlert } from 'react-alert';
import { signInWithEmailAndPassword, signInWithPopup, GoogleAuthProvider, OAuthProvider } from 'firebase/auth';
import validator from 'validator';

import { SocialButton } from '../components/SocialButton';
import { GoogleLogo } from '../components/GoogleLogo';
import { AppleLogo } from '../components/AppleLogo';
import { auth } from '../firebase';
import { Container } from 'react-bootstrap';

export const LoginPage = () => {
  const { t } = useTranslation();
  const alert = useAlert();

  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [errors, setErrors] = useState<any>({});
  const [validated, setValidated] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const emailChanged = (ev: ChangeEvent<HTMLInputElement>) => {
    setEmail(ev.target.value);
    setValidated(false);
  };

  const passwordChanged = (ev: ChangeEvent<HTMLInputElement>) => {
    setPassword(ev.target.value);
    setValidated(false);
  };

  const onFormSubmit = (ev: FormEvent) => {
    ev.preventDefault();

    let validationErrors = {};
    if (!validator.isEmail(email)) {
      validationErrors = { ...validationErrors, email: t('auth.invalid-email') };
    }
    if (password.length < 1) {
      validationErrors = { ...validationErrors, password: t('auth.password-missing') };
    }

    setErrors(validationErrors);
    setValidated(true);

    if (Object.keys(validationErrors).length === 0) {
      setLoading(true);
      signInWithEmailAndPassword(auth, email, password)
        .then(() => {
          setLoading(false);
        })
        .catch((error) => {
          console.error(error);
          setLoading(false);
          if (error.code === 'auth/user-not-found') {
            alert.error(t('auth.user-not-found'));
          } else if (error.code === 'auth/wrong-password') {
            alert.error(t('auth.wrong-password'));
          } else {
            alert.error(t('unknown-error'));
          }
        });
    }
  };

  const loginWithGoogleClicked = () => {
    auth.useDeviceLanguage();
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });
    signInWithPopup(auth, provider).catch((error) => {
      console.error('loginWithGoogleClicked', error);
    });
  };

  const loginWithAppleClicked = () => {
    const provider = new OAuthProvider('apple.com');
    provider.addScope('email');
    provider.addScope('name');
    signInWithPopup(auth, provider).catch((error) => {
      console.error('loginWithAppleClicked', error);
    });
  };

  return (
    <Container>
      <form onSubmit={onFormSubmit} noValidate>
        <h1 className='h3 mb-3'>{t('loginpage.title')}</h1>
        <div className='fw-light mb-3'>{t('loginpage.subtitle')}</div>

        <label className='d-flex mt-3'>{t('email')}</label>
        <input
          autoFocus
          required
          type='email'
          className={['form-control my-2', validated && errors.email ? 'is-invalid' : null].filter((p) => p).join(' ')}
          placeholder={t('email-placeholder')}
          value={email}
          onChange={emailChanged}
        />
        <div className='d-flex invalid-feedback'>{errors.email}</div>

        <label className='d-flex mt-3'>{t('password')}</label>
        <input
          required
          type='password'
          className={['form-control my-2', validated && errors.password ? 'is-invalid' : null]
            .filter((p) => p)
            .join(' ')}
          placeholder={t('password-placeholder')}
          value={password}
          onChange={passwordChanged}
          autoComplete='current-password'
        />
        <div className='d-flex invalid-feedback' dangerouslySetInnerHTML={{ __html: errors.password }} />

        <Link to='/password/reset'>{t('loginpage.forgot-password')}</Link>

        <button className='btn btn-primary w-100 my-3' type='submit' disabled={loading}>
          {t('loginpage.sign-in')}
        </button>

        <div className='d-flex text-center py-3'>
          <hr className='flex-grow-1' />
          <span className='align-self-center mx-3 text-muted text-uppercase'>{t('loginpage.or')}</span>
          <hr className='flex-grow-1' />
        </div>

        <div className='text-center w-100'>
          <SocialButton
            logo={<GoogleLogo />}
            text={t('loginpage.sign-in-with-google')}
            onClick={loginWithGoogleClicked}
          />
          <SocialButton logo={<AppleLogo />} text={t('loginpage.sign-in-with-apple')} onClick={loginWithAppleClicked} />
        </div>

        <div className='text-center text-muted mt-3 w-100'>
          {t('loginpage.no-account')} <Link to='/register'>{t('loginpage.register')}</Link>
        </div>

        <div className='text-center text-muted mt-3 w-100'>
          <a href='https://enzoway.com/' className='btn btn-link'>
            <i className='fa-arrow-right fas me-2' />
            {t('to-homepage')}
          </a>
        </div>
      </form>
    </Container>
  );
};
